import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { helpers, styled } from 'react-free-style';
import classnames from 'classnames';
import {
  StatusInput,
  PhoneInput,
  ContactTypeSelector,
  ContactInput,
  Input,
  fromStringToE164,
  colors as componentColors,
} from '@united-talent-agency/components';
import * as colors from '../../../styles/colors';
import * as elements from '../../../styles/elements';
import * as sizes from '../../../styles/sizes';
import { CALL_FORM, CONTACT_INPUT } from '../../../support/cypressTags';

import {
  TextArea,
  PersonInput,
  DoneButtons,
} from '@united-talent-agency/julius-frontend-components';

import { existingContactPhone, existingContactNotPhone } from '../../../support/contact';
import { useDebouncedCallback } from 'use-debounce';
import withHooksHOC from '../../../hocs/withHooksHOC';
import FormMessage from '../../../components/FormMessage';
import ConfirmationModal, { BoldText } from '../../../components/ConfirmationModal';
import {
  useGetPeopleQuery,
  useUpdateContactMutation,
  useDeleteContactMutation,
  DeskDocument,
  CallRecordsDocument,
} from '../../../generated/graphql';
import DatePicker from '../../../components/CallRow/CallDate/DatePicker';

const CONTACT_PRIORITIES = [
  'Office Phone',
  'Assistant Phone',
  'Mobile Phone',
  'Home Phone',
  'Email',
  'Assistant Email',
  'Personal Email',
  'Message',
  'Fax Number',
  'Unknown',
];

const isOutlookPeople = (people) => people.outlook || people.type === 'Outlook';

/************************************************************
 *         Get Primary Contact from contacts array          *
 * -------------------------------------------------------- *
 *  1. If there are multiple Primary set, the order is:     *
 *     Phone > Email > Other                                *
 *  2. If no priority contact exists, then order will be:   *
 *     Work > Assistant Phone > Mobile > Home > Email >     *
 *     Assistant email > Personal Email > Message > Fax     *
 ************************************************************/
export const getPrimaryContact = (contacts) => {
  let primaryContact = null;
  if (contacts) {
    const primaryContacts = contacts.filter((c) => c.primary);

    if (primaryContacts.length > 0) {
      // has primary contacts
      CONTACT_PRIORITIES.forEach((contactType) => {
        const contact = primaryContacts.find((c) => c.contactType === contactType);
        if (contact && !primaryContact) {
          primaryContact = { ...contact };
          return;
        }
      });
    } else {
      // no primary contacts
      CONTACT_PRIORITIES.forEach((contactType) => {
        const contact = contacts.find((c) => c.contactType === contactType);
        if (contact && !primaryContact) {
          primaryContact = { ...contact };
          return;
        }
      });
    }
  }

  return primaryContact || {};
};

const checkPhoneNumberValidity = (callTodo) => {
  const hasContactInfo = ![null, undefined].includes(callTodo?.contactInfo);

  if (!hasContactInfo) {
    return false;
  }

  const isPhoneType = /Phone|Fax Number/.test(callTodo?.contact?.contactType);

  if (!isPhoneType) {
    return false;
  }

  const e164ContactInfo = fromStringToE164(callTodo.contactInfo);
  const hasValidExtension = ![null, undefined].includes(e164ContactInfo?.extension);
  const hasValidCountryCode = ![null, undefined].includes(e164ContactInfo?.countryCode);
  const hasValidNumber = ![null, undefined].includes(e164ContactInfo?.number);

  const validations = [];
  const [number, extension] = callTodo.contactInfo.split(';');

  // If the phone number is being used, its validation will be added.
  if (number) {
    const isAValidNumber = hasValidCountryCode && hasValidNumber;
    validations.push(isAValidNumber);
  }

  // If the extension is being used, its validation will be added.
  if (extension) {
    validations.push(hasValidExtension);
  }

  return validations.every((value) => !!value);
};

export const validateCallFormRequiredFields = (callTodo, callbackMissingFields) => {
  const missingFields = [];

  if (
    !(callTodo?.recipient || (callTodo?.recipientName && callTodo?.recipientName.trim().length > 0))
  ) {
    missingFields.push('Name');
  }
  if (!callTodo?.status) {
    missingFields.push('Status');
  }
  if (!callTodo?.occurrence_date) {
    missingFields.push('Date');
  }
  if (!callTodo?.contact?.contact) {
    missingFields.push('Contact Info');
  }
  if (!callTodo?.contact?.contactType) {
    missingFields.push('Type');
  }

  if (missingFields.length) {
    callbackMissingFields && callbackMissingFields(missingFields);
    return false;
  }

  return true;
};

const Component = ({
  onChange,
  callTodo = {},
  onSave,
  addToast,
  styles = {},
  statuses,
  onClose,
  desk,
}) => {
  const { refetch } = useGetPeopleQuery({
    skip: true,
  });
  const { editContactsEnabled } = useFlags();
  const [updateContactMutation] = useUpdateContactMutation();
  const [deleteContactMutation] = useDeleteContactMutation();
  const userRegion = useMemo(() => (navigator?.language ?? 'en-US')?.split('-')?.[1], []);

  // Refs
  const phoneInputRef = useRef(null);

  // States
  const [isSaving, setIsSaving] = useState(false);
  // Edit/Delete contact states
  const [editingContact, setEditingContact] = useState(null);
  const [newContactType, setNewContactType] = useState('');
  const [newContactValue, setNewContactValue] = useState('');
  const [contactInputError, setContactInputError] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [personSearchResults, setPersonSearchResults] = useState([]);
  const [outlookSearchResults, setOutlookSearchResults] = useState([]);
  const [deskContactResults, setDeskContactResults] = useState([]);

  const onSelectPerson = useCallback(
    (person) => {
      const updatedCallTodo = {
        ...callTodo,
        recipient: person,
        recipientName: person.name,
        contact: getPrimaryContact(person.contacts),
        companyName: person.companyId && person.companyId.name,
      };

      updatedCallTodo.contactInfo = updatedCallTodo.contact && updatedCallTodo.contact.contact;

      //reset desk address book search results after person select

      setDeskContactResults([]);
      setPersonSearchResults([]);
      setOutlookSearchResults([]);

      callTodo.outlook && (callTodo.outlook.results = []);

      onChange && onChange(updatedCallTodo);
    },
    [callTodo, onChange]
  );

  const searchPerson = useDebouncedCallback((name) => {
    name = name?.trim();

    refetch({
      skip: 0,
      take: 30,
      where: {
        search: name,
        deskOwner: desk?.owner,
        sortBy: 'FIRST_NAME',
      },
    })
      .then((result) => {
        const list = result?.data?.getPeople?.people;
        const nonOutlookContacts = list?.filter((people) => !isOutlookPeople(people));
        const outlookContacts = list?.filter(isOutlookPeople);
        setPersonSearchResults(nonOutlookContacts ?? []);
        setOutlookSearchResults(outlookContacts ?? []);
      })
      .catch((error) => {
        console.error(error);
      });
  }, 500);

  const isPhoneType = useMemo(
    () => callTodo?.contact && /Phone|Fax Number/.test(callTodo?.contact?.contactType),
    [callTodo?.contact]
  );

  const isValidRequiredFields = useMemo(() => validateCallFormRequiredFields(callTodo), [callTodo]);

  const isValidPhoneNumber = useMemo(
    () => !isPhoneType || checkPhoneNumberValidity(callTodo),
    [callTodo, isPhoneType]
  );

  const submit = useCallback(
    async (e) => {
      e.preventDefault();

      const isValid = validateCallFormRequiredFields(callTodo, (_missingFields) => {
        const lf = new Intl.ListFormat('en');
        const formatted = lf.format(_missingFields);
        const areIs = _missingFields.length > 1 ? 'are' : 'is';
        const errorMessage = `${formatted} ${areIs} required to create a Call Record.`;

        addToast && addToast(errorMessage, { appearance: 'error', autoDismiss: true });
      });

      if (!isValid) {
        return;
      }

      if (isPhoneType && callTodo?.contactInfo?.length && !checkPhoneNumberValidity(callTodo)) {
        addToast &&
          addToast('Phone number must be a valid number to create a Call Record', {
            appearance: 'error',
            autoDismiss: true,
          });
        return;
      }

      // Add description as first element of notes
      callTodo.notes = [{ note: callTodo.description || '' }];

      setIsSaving(true);
      // onSave creates endpoint call Promise via call-todo.saveCall
      if (onSave) {
        onSave(callTodo)
          .then(() => {
            phoneInputRef.current?.setCountry(userRegion);
          })
          .finally(() => {
            setIsSaving(false);
          });
      }
    },
    [addToast, callTodo, isPhoneType, onSave, userRegion]
  );

  const isExistingOutlookContact = useCallback(
    () =>
      (callTodo.recipient?.type === 'Outlook' || callTodo.recipient?.outlook) &&
      callTodo.contact?.contact &&
      (existingContactPhone(callTodo.recipient, callTodo.contact) ||
        existingContactNotPhone(callTodo.recipient, callTodo.contact)),
    [callTodo.contact, callTodo.recipient]
  );

  const outlook = { results: outlookSearchResults };

  const currentStatus = useMemo(() => {
    const foundStatus = statuses?.find(({ status }) => status === callTodo.status);

    return foundStatus || statuses?.[0];
  }, [callTodo.status, statuses]);

  callTodo.status = currentStatus && currentStatus.status;

  //combine contacts with desk address book entries (just fall-through logic, should never be both?)
  const existingContacts = (callTodo?.recipient?.contacts || [])
    ?.map(({ _id, contactType = 'Unknown', contact = '', primary }) => ({
      _id,
      contactType,
      value: contact,
      primary,
      private: contactType && !contact,
    }))
    ?.filter(Boolean);

  // Can add contacts - original logic (no feature flag)
  // TODO: When removing editContactsEnabled feature flag, consolidate canAddContact and canEditContact into a single logic
  const canAddContact = useMemo(
    () =>
      !isExistingOutlookContact() &&
      callTodo.recipient?.type !== 'Employee' &&
      (!callTodo.recipient ||
        (callTodo.recipient?._id?.length > 0 && callTodo.recipient?.canEdit) ||
        callTodo.recipient?.type === 'Desk Contact'),
    [callTodo.recipient, isExistingOutlookContact]
  );

  // Can edit/delete contacts - requires feature flag + allowed types
  // TODO: When removing editContactsEnabled feature flag, consolidate canAddContact and canEditContact into a single logic
  const canEditContact = useMemo(() => {
    if (!editContactsEnabled || !canAddContact) {
      return false;
    }

    const recipientType = callTodo.recipient?.type;
    return (
      recipientType === 'Client' ||
      recipientType === 'Industry Contact' ||
      recipientType?.includes('Desk') ||
      recipientType?.includes('Personal')
    );
  }, [editContactsEnabled, canAddContact, callTodo.recipient?.type]);

  // TODO: When removing editContactsEnabled feature flag, remove the flag check and keep only the Outlook message
  // Tooltip logic:
  // - Employee: always show Workday message
  // - Client without permission: show client team message
  // - Flag true + Personal/Desk: can edit, no tooltip shown (canEditContact=true)
  // - Flag true + Outlook: show Outlook-only message
  // - Flag false + any: show original message with both Outlook and Personal
  const tooltipMessage = useMemo(() => {
    if (callTodo.recipient?.type === 'Employee') {
      return 'Contact information for an employee can only be edited (by them) in Workday.';
    }

    if (callTodo.recipient?.type === 'Client' && !canAddContact) {
      return 'Please contact a client team member to make changes to this client profile.';
    }

    if (editContactsEnabled) {
      return 'Phonesheet cannot edit Outlook (O) contacts.';
    }

    return 'Phonesheet cannot edit Outlook (O) or Personal (P) contacts.';
  }, [callTodo.recipient?.type, editContactsEnabled, canAddContact]);

  const handleEditContact = useCallback((contact) => {
    setEditingContact(contact);
    setNewContactType(contact.contactType);
    setNewContactValue(contact.value);
  }, []);

  const handleDeleteContact = useCallback((contact) => {
    setContactToDelete(contact);
    setShowDeleteConfirmation(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingContact(null);
    setNewContactType('');
    setNewContactValue('');
    setContactInputError(null);
  }, []);

  const handleSaveEdit = useCallback(
    async ({ contactType, contact }) => {
      if (!desk?._id || !editingContact?._id) {
        setContactInputError('Unable to update contact');
        return;
      }

      if (!contactType || !contact) {
        setContactInputError('Please enter a valid contact type and value');
        return;
      }

      // Validate phone number format for phone/fax types
      const isPhoneOrFaxType = /Phone|Fax Number/.test(contactType);
      if (isPhoneOrFaxType && contact) {
        const e164Value = fromStringToE164(contact);
        const hasValidCountryCode = ![null, undefined].includes(e164Value?.countryCode);
        const hasValidNumber = ![null, undefined].includes(e164Value?.number);

        if (!hasValidCountryCode || !hasValidNumber) {
          setContactInputError('Please enter a valid phone number');
          return;
        }
      }

      setIsSaving(true);
      try {
        const result = await updateContactMutation({
          variables: {
            deskId: desk._id,
            personId: callTodo.recipient?._id || null,
            contactId: editingContact._id,
            callTodoId: callTodo?._id || null,
            input: {
              contact,
              contactType,
            },
          },
          refetchQueries: [CallRecordsDocument, DeskDocument],
        });

        if (result?.data?.updateContact?.success) {
          setEditingContact(null);
          setNewContactType('');
          setNewContactValue('');
          setContactInputError(null);

          // Update the recipient's contacts list with the edited contact
          const updatedContacts = callTodo.recipient?.contacts?.map((c) =>
            c._id === editingContact._id ? { ...c, contact, contactType } : c
          );

          const updatedCallTodo = {
            ...callTodo,
            recipient: {
              ...callTodo.recipient,
              contacts: updatedContacts,
            },
          };

          // Also update the selected contact if it was the one being edited
          if (callTodo.contact?._id === editingContact._id) {
            updatedCallTodo.contact = { ...callTodo.contact, contact, contactType };
            updatedCallTodo.contactInfo = contact;
          }

          onChange && onChange(updatedCallTodo);
          addToast &&
            addToast('Contact updated successfully.', { appearance: 'success', autoDismiss: true });
        } else {
          setContactInputError(result?.data?.updateContact?.message || 'Failed to update contact');
          addToast &&
            addToast('Failed to update contact. Please try again.', {
              appearance: 'error',
              autoDismiss: true,
            });
        }
      } catch (error) {
        console.error('Error updating contact:', error);
        setContactInputError('Failed to update contact');
        addToast &&
          addToast('Failed to update contact. Please try again.', {
            appearance: 'error',
            autoDismiss: true,
          });
      } finally {
        setIsSaving(false);
      }
    },
    [addToast, callTodo, desk, editingContact, onChange, updateContactMutation]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!desk?._id || !contactToDelete?._id) {
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteContactMutation({
        variables: {
          deskId: desk._id,
          personId: callTodo.recipient?._id || null,
          contactId: contactToDelete._id,
          callTodoId: callTodo?._id || null,
        },
        refetchQueries: [CallRecordsDocument, DeskDocument],
      });

      if (result?.data?.deleteContact?.success) {
        setContactToDelete(null);
        setShowDeleteConfirmation(false);

        // Remove the deleted contact from the recipient's contacts list
        const updatedContacts = callTodo.recipient?.contacts?.filter(
          (c) => c._id !== contactToDelete._id
        );

        const updatedCallTodo = {
          ...callTodo,
          recipient: {
            ...callTodo.recipient,
            contacts: updatedContacts,
          },
        };

        // If the deleted contact was the selected one, select another contact or clear it
        if (callTodo.contact?._id === contactToDelete._id) {
          // Try to select the next available contact using the same priority logic
          const nextContact = getPrimaryContact(updatedContacts);
          // Check if nextContact has an _id (meaning it's an existing contact from the list)
          if (nextContact && nextContact._id) {
            updatedCallTodo.contact = nextContact;
            updatedCallTodo.contactInfo = nextContact.contact || '';
          } else {
            // No contacts left, set to default type (Office Phone) like initial state
            updatedCallTodo.contact = { contactType: 'Office Phone', contact: '' };
            updatedCallTodo.contactInfo = '';
          }
        }

        onChange && onChange(updatedCallTodo);
        addToast &&
          addToast('Contact deleted successfully.', { appearance: 'success', autoDismiss: true });
      } else {
        console.error('Failed to delete contact:', result?.data?.deleteContact?.message);
        addToast &&
          addToast('Failed to delete contact. Please try again.', {
            appearance: 'error',
            autoDismiss: true,
          });
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      addToast &&
        addToast('Failed to delete contact. Please try again.', {
          appearance: 'error',
          autoDismiss: true,
        });
    } finally {
      setIsDeleting(false);
    }
  }, [addToast, callTodo, contactToDelete, desk, onChange, deleteContactMutation]);

  const handleCancelDelete = useCallback(() => {
    setContactToDelete(null);
    setShowDeleteConfirmation(false);
  }, []);

  const handleOnChangeContactInput = useCallback(({ contactType, contact }) => {
    setNewContactType(contactType);
    setNewContactValue(contact);
    setContactInputError(null);
  }, []);

  const handleOnChangePersonInput = useCallback(
    (value) => {
      callTodo.recipient = null;
      callTodo.recipientName = value;

      value && searchPerson(value);
      onChange && onChange(callTodo);
    },
    [callTodo, onChange, searchPerson]
  );

  const handleOnChangePhoneInput = (value) => {
    delete callTodo.contact._id;
    const existing = existingContactPhone(callTodo.recipient, callTodo.contact);
    if (existing) {
      callTodo.contact._id = existing._id;
    }

    onChange &&
      onChange({
        ...callTodo,
        contactInfo: value,
        contact: {
          ...callTodo?.contact,
          contact: value,
        },
      });
  };

  const phoneNumberIsValid = useMemo(() => checkPhoneNumberValidity(callTodo), [callTodo]);

  return (
    <form
      data-valid={isValidRequiredFields && isValidPhoneNumber}
      className={styles.container}
      onSubmit={submit}
      data-cy={CALL_FORM.CALL_FORM}
    >
      <div className={styles.mainContainer}>
        <div className={styles.subContainer}>
          <div className={styles.statusAndNameWrapper}>
            <div
              data-cy={CALL_FORM.STATUS_TEXT_BOX}
              className={classnames(styles.section, styles.statusInput)}
            >
              {!!statuses && (
                <StatusInput
                  options={statuses.filter((code) => code.name && code.status)}
                  value={currentStatus}
                  onChange={(value) => {
                    onChange &&
                      onChange({
                        ...callTodo,
                        status: value.status,
                      });
                  }}
                />
              )}
            </div>

            <div
              data-cy={CALL_FORM.NAME_TEXT_BOX}
              className={classnames(styles.section, styles.nameInput)}
            >
              <PersonInput
                focus
                results={[...personSearchResults, ...deskContactResults]}
                outlook={outlook}
                showOutlook={true}
                value={callTodo.recipientName ?? ''}
                onChange={handleOnChangePersonInput}
                onSelect={onSelectPerson}
              />
            </div>
          </div>

          <div className={classnames(styles.section, styles.contactInput)}>
            {editingContact ? (
              <ContactInput
                focused
                editMode
                loading={isSaving}
                contact={{ contact: newContactValue, contactType: newContactType }}
                onChange={handleOnChangeContactInput}
                onCancel={handleCancelEdit}
                onSave={handleSaveEdit}
                onBlur={() => {}}
                error={contactInputError}
                outline={false}
                containerStyle={{
                  border: `1px solid ${componentColors.nobel}`,
                  boxShadow: '0 2px 10px rgba(173, 165, 159, 0.3)',
                }}
                cypressTags={{
                  existingContact: CONTACT_INPUT.EXISTING_CONTACT,
                  addContact: CONTACT_INPUT.ADD_CONTACT,
                  contactType: CONTACT_INPUT.CONTACT_TYPE,
                  phoneNumberInput: CONTACT_INPUT.PHONE_NUMBER_INPUT,
                }}
              />
            ) : (
              <>
                <div
                  id="contact-type-selector"
                  data-cy={CALL_FORM.TYPE_SELECT_BOX}
                  className={classnames({
                    [classnames('invalid', styles.invalidFieldContainer)]:
                      !callTodo?.contact?.contactType ||
                      callTodo?.contact?.contactType === 'Unknown',
                  })}
                >
                  <ContactTypeSelector
                    value={{
                      contactType: callTodo?.contact?.contactType || 'Unknown',
                      value: callTodo?.contact?.contact,
                    }}
                    existing={existingContacts}
                    onChange={({ contactType = 'Unknown', value, _id }) => {
                      if (onChange) {
                        onChange({
                          ...callTodo,
                          contact: { contactType, contact: value, _id },
                          contactInfo: value,
                        });
                      }
                    }}
                    extSeparator=" x"
                    addable={canAddContact}
                    canEdit={canEditContact}
                    onEdit={handleEditContact}
                    onDelete={handleDeleteContact}
                    tooltipMessage={tooltipMessage}
                    cypressTags={{
                      existingContact: CONTACT_INPUT.EXISTING_CONTACT,
                      addContact: CONTACT_INPUT.ADD_CONTACT,
                      contactType: CONTACT_INPUT.CONTACT_TYPE,
                    }}
                  />
                </div>
                {callTodo?.contact ? (
                  isPhoneType ? (
                    <div
                      id="phone-or-fax-input"
                      data-cy={CALL_FORM.PHONE_TEXT_BOX}
                      className={classnames(styles.phoneOrFaxInputContainer, {
                        [classnames('invalid', styles.invalidFieldContainer)]:
                          !callTodo?.contactInfo || !phoneNumberIsValid,
                      })}
                    >
                      <PhoneInput
                        ref={(ref) => (phoneInputRef.current = ref)}
                        userRegion={userRegion}
                        value={callTodo?.contactInfo}
                        title={callTodo.contact.contactType}
                        onChange={handleOnChangePhoneInput}
                        disabled={!canAddContact}
                        cypressTags={{
                          phoneNumberInput: CONTACT_INPUT.PHONE_NUMBER_INPUT,
                        }}
                      />

                      {!phoneNumberIsValid && !!callTodo?.contactInfo && (
                        <FormMessage>Invalid Number</FormMessage>
                      )}
                    </div>
                  ) : (
                    <div
                      id="non-phone-or-fax-input"
                      data-cy={CALL_FORM.NORMAL_TEXT_BOX}
                      style={{ minWidth: 250, flex: 1 }}
                      className={classnames({
                        [classnames('invalid', styles.invalidFieldContainer)]:
                          !callTodo?.contactInfo,
                      })}
                    >
                      <Input
                        /**
                         * The value should always be a string, because if it comes as undefined,
                         * it will prevent the primitive input value from changing and the label will remain over the value.
                         */
                        value={callTodo?.contactInfo ?? ''}
                        title={callTodo.contact.contactType}
                        onChange={(value) => {
                          const existing = existingContactNotPhone(
                            callTodo.recipient,
                            callTodo.contact
                          );

                          const updatedCallTodo = {
                            ...callTodo,
                            contactInfo: value,
                            contact: {
                              ...callTodo.contact,
                              contact: value,
                              _id: existing && existing._id ? existing._id : undefined,
                            },
                          };

                          onChange && onChange(updatedCallTodo);
                        }}
                        disabled={!canAddContact}
                      />
                    </div>
                  )
                ) : (
                  <></>
                )}
              </>
            )}
          </div>
        </div>
        <div className={styles.subContainer}>
          <div
            data-cy={CALL_FORM.DATE_TEXT_BOX}
            className={classnames(styles.section, styles.datetimePicker, {
              [styles.invalidDatetimePicker]: !callTodo?.occurrence_date,
            })}
          >
            <DatePicker
              initWithCurrentDate
              occurrenceDate={callTodo?.occurrence_date}
              onChange={(date) => {
                onChange && onChange({ ...callTodo, occurrence_date: date?.getTime?.() });
              }}
            />
          </div>

          <div
            data-cy={CALL_FORM.NOTES_TEXT_BOX}
            className={classnames(styles.section, styles.notesInput)}
          >
            <TextArea
              key="notes"
              id="notes-input"
              title="Notes"
              onChange={(notes) => {
                onChange && onChange({ ...callTodo, description: notes });
              }}
              value={callTodo.description}
            />
          </div>

          <div className={styles.doneButtons}>
            <DoneButtons
              isActive
              isSaving={isSaving}
              onDone={() => {
                onClose && onClose();
                if (phoneInputRef.current) {
                  phoneInputRef.current.setCountry(userRegion);
                }
              }}
              cypressSaveTag={CALL_FORM.CHECKMARK_BUTTON}
              cypressCloseTag={CALL_FORM.CANCEL_BUTTON}
            />
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation && !!contactToDelete}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Confirmation"
        loading={isDeleting}
      >
        Delete <BoldText>{contactToDelete?.contactType}</BoldText>: {contactToDelete?.value}?
      </ConfirmationModal>
    </form>
  );
};

const withStyles = styled({
  container: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    containerName: 'call-form-container',
    containerType: 'inline-size',

    ['&[data-valid="false"] #done-button']: {
      cursor: 'not-allowed',
    },
  },
  mainContainer: {
    display: 'flex',
    alignItems: 'start',
    flex: 1,
    background: colors.contentBackground,
    border: `1px solid ${colors.darkBorder}`,
    padding: 6,
    gap: 6,
    [`@container call-form-container (max-width: 1025px)`]: {
      flexWrap: 'wrap',
    },
    [`@container call-form-container (max-width: 768px)`]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  subContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    position: 'relative',
    [`@container call-form-container ${sizes.mobileBreakpoint}`]: {
      flexWrap: 'wrap',
    },
    [`@container call-form-container (max-width: 425px)`]: {
      flexDirection: 'column',
      alignItems: 'normal',
    },
    '&:first-child': {
      zIndex: 2,
    },
  },
  section: {},
  button: helpers.merge(elements.reset, elements.actionable, {
    padding: 10,
    lineHeight: 0,
  }),
  statusAndNameWrapper: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    alignSelf: 'start',
    [`@container call-form-container (max-width: 425px)`]: {
      alignSelf: 'stretch',
    },
  },
  statusInput: {
    width: 70,
  },
  nameInput: {
    flex: 1,
    minWidth: 150,
    width: '100%',
  },
  contactInput: {
    display: 'flex',
    flex: 1,
    marginTop: '0px!important',
    position: 'relative',
    overflow: 'visible',
    zIndex: 3,

    ['& .invalid ~ .invalid input']: {
      borderLeftColor: 'transparent',
    },
    ['& .invalid ~ .invalid button']: {
      borderLeftColor: 'transparent',
    },
    ['& #phone-or-fax-input.invalid input']: {
      borderLeftColor: 'transparent',
    },
  },
  phoneOrFaxInputContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    gap: 2,
    width: '100%',
  },
  invalidFieldContainer: {
    ['& button']: {
      borderColor: '#CB0B2F',
    },
    ['& input']: {
      borderColor: '#CB0B2F',
    },
  },
  datetimePicker: {
    position: 'relative',
    display: 'flex',
    minWidth: 170,

    '&>button': {
      display: 'flex',
      flex: 1,
    },

    '&>div': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      top: '100%',
    },
  },
  invalidDatetimePicker: {
    ['& button']: {
      borderColor: '#CB0B2F',
    },
  },
  notesInput: {
    flex: 1,
    minWidth: 200,
    width: '100%',
    '&&>*': {
      display: 'flex',
    },
  },
  doneButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: '0!important',
    marginBottom: '0!important',
    marginLeft: 'auto',
    '&&>*': {
      justifyContent: 'center',
      margin: 0,
    },
  },
  alertContainer: {
    textAlign: 'center',
    width: '450px',
    fontSize: '12px',
    background: 'white',
    opacity: 1,
    padding: '40px',
    boxShadow: '0 20px 75px rgba(0, 0, 0, 0.23)',
    color: '#4a4a4a',
    border: '1px solid #4a4a4a',
  },
  alertButton: helpers.merge(elements.button, elements.actionable, {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    borderColor: '#000',
    margin: '5px',
  }),
  private: {
    position: 'absolute',
  },
  privateLabel: {
    fontSize: 12,
    fontWeight: '100',
    display: 'block',
    position: 'relative',
    cursor: 'pointer',
    userSelect: 'none',
    ' input': {
      position: 'absolute',
      opacity: 0,
      cursor: 'pointer',
    },
    ' input:checked': {
      display: 'block',
    },
    ' .privateCheckbox:after': {
      left: '9px',
      top: '5px',
      width: '5px',
      height: '10px',
      border: 'solid white',
      borderWidth: '0 3px 3px 0',
      transform: 'rotate(45deg)',
    },
  },
  privateCheckbox: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: 15,
    width: 15,
    backgroundColor: '#eee',
    '&:after': {
      content: '""',
      position: 'absolute',
      display: 'none',
    },
  },
});

const CallForm = withStyles(withHooksHOC(Component));

export default CallForm;
